/**
 * Narration text for each modexp step. Plain English, one sentence per step.
 *
 * Kept separate from the algorithm so we can tweak prose without touching
 * the math, and so the algorithm file stays readable as code.
 *
 * Factory pattern: bind the inputs once, get a describer that has access
 * to the base/mod for steps that don't carry them inline.
 *
 * Uses unicode superscripts so the narration text aligns with the visual
 * (which uses HTML <sup>) without requiring HTML rendering in the card.
 */

import type { ModexpStep, ModexpInputs } from './types';

const SUPERSCRIPT_DIGITS: Record<string, string> = {
  '0': '⁰',
  '1': '¹',
  '2': '²',
  '3': '³',
  '4': '⁴',
  '5': '⁵',
  '6': '⁶',
  '7': '⁷',
  '8': '⁸',
  '9': '⁹',
};

function sup(n: bigint | number): string {
  return String(n)
    .split('')
    .map((d) => SUPERSCRIPT_DIGITS[d] ?? d)
    .join('');
}

export function makeModexpDescriber(
  inputs: ModexpInputs,
): (step: ModexpStep) => string {
  const { base, exp, mod } = inputs;
  const fullExpression = `${base}${sup(exp)} mod ${mod}`;

  return function describe(step: ModexpStep): string {
    switch (step.kind) {
      case 'start':
        return `Compute ${fullExpression} using square-and-multiply.`;

      case 'binary':
        return `Write ${exp} in binary as ${step.bits}. We scan it from left to right.`;

      case 'inspect-bit': {
        if (step.bitIndex === 0) {
          return `First bit is ${step.bit}. Initialize the running result to ${step.result}.`;
        }
        return `Look at bit ${step.bitIndex + 1}. It is ${step.bit}.`;
      }

      case 'square':
        return `Square the running result: ${step.previousResult}² mod ${mod} = ${step.result}.`;

      case 'multiply':
        return `Bit is 1, so multiply by ${base}: ${step.previousResult} × ${base} mod ${mod} = ${step.result}.`;

      case 'done':
        return `Done. ${fullExpression} = ${step.result}.`;
    }
  };
}
